import {
  ComponentType,
  FC,
  memo,
  useEffect,
  useMemo,
  useState,
  MouseEvent,
} from 'react'
import { useTranslation } from 'react-i18next'
import { CircularProgress, IconButton, Menu, MenuItem } from '@mui/material'
import AutoSizer from 'react-virtualized-auto-sizer'
import {
  FixedSizeList as _FixedSizeList,
  FixedSizeListProps,
  ListChildComponentProps,
} from 'react-window'
import { useDebouncedCallback } from 'use-debounce'
import CloseIcon from '@/core/assets/icons/close.svg'
import ChevronDownIcon from '@/core/assets/icons/chevron-down.svg'
import SearchIcon from '@/core/assets/icons/search.svg'
import AddIcon from '@/core/assets/icons/add.svg'
import { ProductCard } from '@/ui/components/ProductCard'
import { SearchInput } from '@/ui/components/SearchInput'
import { NothingFoundPlaceholder } from '@/ui/components/NothingFoundPlaceholder'
import { Product } from '@/data/replenishmentPlan/slice/replenishmentPlan.types.ts'
import {
  Wrapper,
  Header,
  Title,
  FilterWrapper,
  DivisionRow,
  SearchRow,
  SearchInputWrapper,
  DivisionSelectorWrapper,
  DivisionName,
  SearchIconWrapper,
  ProductList,
  ProductItem,
  LoaderWrapper,
  PlaceholderWrapper,
  StyledIconButton,
} from './ImportFromInventoryDrawer.styles.ts'

const FixedSizeList = _FixedSizeList as ComponentType<FixedSizeListProps>

interface Division {
  id: number
  name: string
}

interface Props {
  divisions: Division[]
  products: Product[]
  isLoading: boolean
  isAddingProduct: boolean
  onClose: () => void
  onSearch: (term: string, divisionId?: number) => void
  onAddProduct: (product: Product) => void
}

interface ProductRowData {
  products: Product[]
  isAddingProduct: boolean
  onAddProduct: (product: Product) => void
}

const getItemKey = (index: number, data: ProductRowData) =>
  data.products[index]?.id ?? index

const ProductRow = memo(
  ({ data, index, style }: ListChildComponentProps<ProductRowData>) => {
    const product = data.products[index]

    if (!product) {
      return null
    }

    return (
      <ProductItem style={style as never}>
        <ProductCard
          product={product}
          backgroundColor="transparent"
          actions={
            <StyledIconButton
              size="small"
              disabled={data.isAddingProduct}
              onClick={() => data.onAddProduct(product)}
            >
              <AddIcon />
            </StyledIconButton>
          }
        />
      </ProductItem>
    )
  },
)

const ImportFromInventoryDrawer: FC<Props> = ({
  divisions,
  products,
  isLoading,
  isAddingProduct,
  onClose,
  onSearch,
  onAddProduct,
}) => {
  const { t } = useTranslation()

  const [selectedDivision, setSelectedDivision] = useState<Division | null>(
    null,
  )
  const [divisionMenuAnchor, setDivisionMenuAnchor] =
    useState<HTMLElement | null>(null)
  const [searchValue, setSearchValue] = useState('')

  const debouncedSearch = useDebouncedCallback(
    (value: string, divisionId?: number) => {
      onSearch(value, divisionId)
    },
    500,
  )

  const itemData = useMemo<ProductRowData>(
    () => ({
      products,
      isAddingProduct,
      onAddProduct,
    }),
    [products, isAddingProduct, onAddProduct],
  )

  useEffect(() => {
    onSearch('', undefined)
  }, [])

  const onSearchChange = (value: string) => {
    setSearchValue(value)
    debouncedSearch(value, selectedDivision?.id)
  }

  const onDivisionClick = (event: MouseEvent<HTMLElement>) => {
    setDivisionMenuAnchor(event.currentTarget)
  }

  const onDivisionMenuClose = () => {
    setDivisionMenuAnchor(null)
  }

  const onDivisionSelect = (division: Division | null) => {
    setSelectedDivision(division)
    setDivisionMenuAnchor(null)
    onSearch(searchValue, division?.id)
  }

  const canShowProducts = products.length > 0
  const showNoResults = !isLoading && products.length === 0

  return (
    <Wrapper>
      <Header>
        <Title>{t('importFromInventoryDrawer.title')}</Title>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Header>

      <FilterWrapper>
        <DivisionRow>
          <DivisionSelectorWrapper onClick={onDivisionClick}>
            <DivisionName>
              {selectedDivision?.name ??
                t('importFromInventoryDrawer.allDivisions')}
            </DivisionName>
            <ChevronDownIcon />
          </DivisionSelectorWrapper>
        </DivisionRow>
        <SearchRow>
          <SearchInputWrapper>
            <SearchInput
              value={searchValue}
              placeholder={t('importFromInventoryDrawer.searchPlaceholder')}
              onChange={onSearchChange}
              hideIcon
            />
          </SearchInputWrapper>
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
        </SearchRow>
      </FilterWrapper>

      <Menu
        anchorEl={divisionMenuAnchor}
        open={Boolean(divisionMenuAnchor)}
        onClose={onDivisionMenuClose}
        PaperProps={{
          sx: { maxHeight: 300, width: 280 },
        }}
      >
        <MenuItem onClick={() => onDivisionSelect(null)}>
          {t('importFromInventoryDrawer.allDivisions')}
        </MenuItem>
        {divisions.map((division) => (
          <MenuItem
            key={division.id}
            onClick={() => onDivisionSelect(division)}
          >
            {division.name}
          </MenuItem>
        ))}
      </Menu>

      <ProductList>
        {isLoading ? (
          <LoaderWrapper>
            <CircularProgress />
          </LoaderWrapper>
        ) : canShowProducts ? (
          <AutoSizer>
            {({ width, height }) => (
              <FixedSizeList
                height={height}
                width={width}
                itemSize={88}
                itemCount={products.length}
                overscanCount={5}
                itemData={itemData}
                itemKey={getItemKey}
              >
                {ProductRow}
              </FixedSizeList>
            )}
          </AutoSizer>
        ) : showNoResults ? (
          <PlaceholderWrapper>
            <NothingFoundPlaceholder />
          </PlaceholderWrapper>
        ) : null}
      </ProductList>
    </Wrapper>
  )
}

export default ImportFromInventoryDrawer
